// src/app/videos/CuratedVideoCard.tsx
//
// One outside video on a curated collection page. Click-to-load like EpisodePlayer: the
// card is a thumbnail until someone presses play, so a page of twenty embeds costs no
// YouTube requests for the visitor who only came to scan the list. The embed is the
// privacy-enhanced youtube-nocookie player the lessons use (VideoSection).
//
// The channel is on the card before AND after play. These are not our videos, and the
// library must never read as if they were.

'use client'

import { useState } from 'react'

export type CuratedCardVideo = {
  youtubeId: string
  title: string
  source: string
  duration: string
}

export default function CuratedVideoCard({ video }: { video: CuratedCardVideo }) {
  const [playing, setPlaying] = useState(false)
  const watchUrl = `https://www.youtube.com/watch?v=${video.youtubeId}`

  return (
    <div className="flex h-full flex-col overflow-hidden rounded-xl border border-border bg-card/40">
      <div className="relative aspect-video w-full bg-muted">
        {playing ? (
          <iframe
            className="absolute inset-0 h-full w-full"
            src={`https://www.youtube-nocookie.com/embed/${video.youtubeId}?rel=0&autoplay=1`}
            title={`${video.title} — ${video.source}`}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        ) : (
          <button
            type="button"
            onClick={() => setPlaying(true)}
            aria-label={`Play “${video.title}” from ${video.source}`}
            className="group absolute inset-0 focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand"
          >
            <img
              src={`https://img.youtube.com/vi/${video.youtubeId}/mqdefault.jpg`}
              alt=""
              loading="lazy"
              className="h-full w-full object-cover"
            />
            <span className="absolute inset-0 flex items-center justify-center bg-black/15 transition group-hover:bg-black/30">
              <span className="flex h-12 w-12 items-center justify-center rounded-full bg-black/70 text-white shadow-lg transition group-hover:scale-110">
                <svg
                  className="h-5 w-5 translate-x-0.5"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                  aria-hidden="true"
                >
                  <path d="M6.3 2.841A1.5 1.5 0 004 4.11v11.78a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z" />
                </svg>
              </span>
            </span>
            <span className="absolute bottom-2 right-2 rounded bg-black/75 px-1.5 py-0.5 font-mono text-[11px] font-medium text-white">
              {video.duration}
            </span>
          </button>
        )}
      </div>
      <div className="flex flex-1 flex-col p-4">
        <p className="text-xs font-semibold text-muted-foreground">From {video.source}</p>
        <p className="mt-1 font-semibold leading-snug">{video.title}</p>
        <a
          href={watchUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-auto pt-3 text-xs font-medium text-brand-ink hover:underline"
        >
          Watch on YouTube ↗
        </a>
      </div>
    </div>
  )
}
